import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { lineKey as sharedLineKey } from "@/lib/cartKey";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("pd_cart")) || [];
    } catch {
      return [];
    }
  });
  const [discount, setDiscount] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("pd_discount")) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    localStorage.setItem("pd_cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem("pd_discount", JSON.stringify(discount));
  }, [discount]);

  // Delegates to the shared lineKey helper so cart+drawer+checkout all
  // key by the same rule (product_id + variant + addon signature).
  const keyFor = (product_id, variant_pack_size = null, addons = []) =>
    sharedLineKey({ product_id, variant_pack_size, addons });

  // Flyout drawer state — opens on every addItem() so the user gets a
  // dopamine hit + cross-sell surface without losing their place. The cart
  // icon in the header still routes to the full /cart page.
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [lastAddedKey, setLastAddedKey] = useState(null);

  const addItem = (product, qty = 1, variant = null, addons = []) => {
    // variant = { pack_size, label, price } — for accessories with pack-size variants.
    // addons  = [{ sku, name, price }] — modifiers on the same cart line
    //   (e.g. Recon Kit on Raw Vial, Travel Case on Pen Kit). Add-on
    //   price rolls into the line price for cart math.
    const addonSubtotal = (addons || []).reduce((s, a) => s + (Number(a.price) || 0), 0);
    const basePrice = variant ? variant.price : product.price;
    const line = {
      product_id: product.id,
      slug: product.slug,
      name: variant ? `${product.name} · ${variant.label}` : product.name,
      price: basePrice + addonSubtotal,
      base_price: basePrice,
      image: product.image,
      quantity: qty,
      variant_pack_size: variant ? variant.pack_size : null,
      addons: addons || [],
      // Extra metadata carried on the line item so downstream UI (e.g. the
      // smart BAC water suggestion on the cart) can reason about it without
      // an extra network round-trip.
      format: product.format || null,
      size_mg: product.size_mg ?? null,
      is_accessory: !!product.is_accessory,
    };
    const lineKey = keyFor(line.product_id, line.variant_pack_size, line.addons);
    setItems((prev) => {
      const existing = prev.find(
        (i) => keyFor(i.product_id, i.variant_pack_size, i.addons) === lineKey
      );
      if (existing) {
        return prev.map((i) =>
          keyFor(i.product_id, i.variant_pack_size, i.addons) === lineKey
            ? { ...i, quantity: i.quantity + qty }
            : i
        );
      }
      return [...prev, line];
    });
    setLastAddedKey(lineKey);
    setFlyoutOpen(true);
    toast.success(`${line.name} added to cart`);
  };

  const closeFlyout = () => setFlyoutOpen(false);

  const updateQty = (lineKey, qty) => {
    if (qty <= 0) return removeItem(lineKey);
    setItems((prev) => prev.map((i) => (keyFor(i.product_id, i.variant_pack_size, i.addons) === lineKey ? { ...i, quantity: qty } : i)));
  };

  const removeItem = (lineKey) => {
    setItems((prev) => prev.filter((i) => keyFor(i.product_id, i.variant_pack_size, i.addons) !== lineKey));
  };

  // Add a line-level modifier (add-on) to an EXISTING cart line — same
  // shape the PDP toggle produces (`{ sku, name, price }`). Used by the
  // cart drawer's "Add Recon Kit" prompt so it lands on the same line
  // as the base product instead of creating a separate cart entry.
  //
  // Because addons are part of the cart lineKey signature, adding one
  // changes the key. If a line with the new key already exists (e.g.
  // buyer had already added the same vial with a Recon Kit toggle from
  // the PDP), quantities are merged and the current line is dropped.
  // Returns the resulting lineKey so callers can update `lastAddedKey`.
  const addAddonToLine = (currentLineKey, addon) => {
    if (!addon || !addon.sku) return null;
    let nextKey = currentLineKey;
    setItems((prev) => {
      const existing = prev.find(
        (i) => keyFor(i.product_id, i.variant_pack_size, i.addons) === currentLineKey
      );
      if (!existing) return prev;
      if ((existing.addons || []).some((a) => a.sku === addon.sku)) {
        // Already present — no-op (drawer should suppress the prompt
        // when this is the case, this is just a safety net).
        return prev;
      }
      const nextAddons = [
        ...(existing.addons || []),
        { sku: addon.sku, name: addon.name, price: Number(addon.price) || 0 },
      ];
      nextKey = keyFor(existing.product_id, existing.variant_pack_size, nextAddons);
      const priceDelta = Number(addon.price) || 0;
      const alreadyMerged = prev.find(
        (i, idx) =>
          prev.indexOf(existing) !== idx &&
          keyFor(i.product_id, i.variant_pack_size, i.addons) === nextKey
      );
      if (alreadyMerged) {
        return prev
          .filter((i) => keyFor(i.product_id, i.variant_pack_size, i.addons) !== currentLineKey)
          .map((i) =>
            keyFor(i.product_id, i.variant_pack_size, i.addons) === nextKey
              ? { ...i, quantity: i.quantity + existing.quantity }
              : i
          );
      }
      return prev.map((i) =>
        keyFor(i.product_id, i.variant_pack_size, i.addons) === currentLineKey
          ? { ...i, addons: nextAddons, price: (i.price || 0) + priceDelta }
          : i
      );
    });
    setLastAddedKey(nextKey);
    return nextKey;
  };

  const clearCart = () => {
    setItems([]);
    setDiscount(null);
  };

  const restoreCart = (restoredItems) => {
    setItems(restoredItems);
  };

  // Discount shape: { code, discount_type: "percentage" | "fixed", discount_value: number }
  const applyDiscount = (code, discount_type, discount_value) =>
    setDiscount({ code, discount_type, discount_value });
  const clearDiscount = () => setDiscount(null);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const discountAmount = useMemo(() => {
    if (!discount) return 0;
    if (discount.discount_type === "fixed") {
      return Math.min(Number(discount.discount_value) || 0, subtotal);
    }
    // default: percentage (supports legacy { percent } shape too)
    const pct = Number(discount.discount_value ?? discount.percent) || 0;
    return (subtotal * pct) / 100;
  }, [subtotal, discount]);
  const total = Math.max(0, subtotal - discountAmount);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateQty,
        removeItem,
        addAddonToLine,
        clearCart,
        restoreCart,
        discount,
        applyDiscount,
        clearDiscount,
        subtotal,
        discountAmount,
        total,
        count,
        flyoutOpen,
        closeFlyout,
        lastAddedKey,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
